import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isAuthConfigured } from './config';
import { useCurrentStaff } from './useCurrentStaff';
import type { StaffPermission } from './staffPermissions';
import { Forbidden } from '@/routes/Forbidden';

/**
 * Route guard for a staff permission (M0-33).
 *
 * ⚠️ This is a usability feature, not a security control. It stops someone
 * being shown a screen they cannot use; it does not stop them reaching the
 * data, because the data only ever arrives from an API that checks the same
 * `staff:` permission server-side. Never move a check *out* of the API on the
 * grounds that the console already does it.
 *
 * M1-32's acceptance criteria put this plainly for the case that matters: a
 * Support Technician reaching for global diagnostics scope must meet the API's
 * real 403. Hiding the control is a courtesy; the 403 is the enforcement.
 *
 * Inert when auth is not configured, matching `RequireAuth` — otherwise a
 * local checkout with no staff app registration could not open any screen.
 */
export function RequireStaffPermission({
  permission,
  children,
}: {
  permission: StaffPermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { data, isPending, isError } = useCurrentStaff();

  if (!isAuthConfigured) {
    return <>{children}</>;
  }

  if (isPending) {
    return (
      <p className="text-sm text-[var(--color-muted-foreground)]" role="status">
        {t('requirePermission.checkingAccess')}
      </p>
    );
  }

  // An error here is most often a 403: authenticated against the workforce
  // tenant, but not enrolled as staff. Same outcome as lacking the permission.
  // Optional chain on `permissions` too, not just `data`: a response that is
  // not the shape we expect must fail closed, not throw and take the shell
  // down.
  if (isError || !data?.permissions?.includes(permission)) {
    return <Forbidden />;
  }

  return <>{children}</>;
}

/** Imperative form, for hiding controls rather than whole routes. */
export function useHasStaffPermission(): (permission: StaffPermission) => boolean {
  const { data } = useCurrentStaff();
  return (permission) => !isAuthConfigured || (data?.permissions?.includes(permission) ?? false);
}
